import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, EmptyState, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import { ListFooter } from '@/components/ListPager';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { usePermissions } from '@/hooks/usePermissions';
import { api, apiFormData, listResource } from '@/lib/api';
import { mediaDeleteSuccessToast, mediaUploadSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';

interface MediaItem {
  id: string;
  title?: string;
  name?: string;
  filename?: string;
  mimeType?: string;
  type?: string;
  size?: number | string;
  humanSize?: string;
  thumbnailUrl?: string;
  versionCount?: number;
}

const TYPE_CHIPS = [
  { value: '', label: 'Todos' },
  { value: 'image', label: 'Imagen' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'pdf', label: 'PDF' },
  { value: 'document', label: 'Documento' },
];

const TYPE_ICONS: Record<string, string> = {
  image: 'image',
  video: 'movie',
  audio: 'audio_file',
  pdf: 'picture_as_pdf',
  document: 'description',
};

function formatSize(bytes?: number | string, human?: string) {
  if (human) return human;
  const value = typeof bytes === 'string' ? Number(bytes) : bytes;
  if (!value) return '—';
  if (value < 1024 * 1024) return `${Math.round(value / 1024)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
}

function displayName(file: MediaItem) {
  return file.title ?? file.name ?? file.filename ?? 'Archivo';
}

export function MediaPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canDelete = hasPermission(PERMISSIONS.MULTIMEDIA_ELIMINAR);
  const canEdit = hasPermission(PERMISSIONS.MULTIMEDIA_EDITAR);
  const canUpload = hasPermission(PERMISSIONS.MULTIMEDIA_CREAR);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState('');
  const [activeType, setActiveType] = useState('');
  const [page, setPage] = useState(1);
  const appliedSearch = useDebouncedValue(search, 300).trim();

  useEffect(() => {
    setPage(1);
  }, [appliedSearch, activeType]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState<MediaItem | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['media', appliedSearch, activeType, page],
    queryFn: () =>
      listResource<MediaItem>('/media', {
        page,
        perPage: 30,
        ...(appliedSearch ? { search: appliedSearch } : {}),
        ...(activeType ? { type: activeType } : {}),
      }),
  });

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      for (let index = 0; index < files.length; index += 1) {
        const formData = new FormData();
        formData.append('file', files[index]!);
        await apiFormData('/media/upload', formData);
        setUploadProgress(Math.round(((index + 1) / files.length) * 100));
      }
      return files.length;
    },
    onSuccess: async (count) => {
      setUploadProgress(0);
      toast(mediaUploadSuccessToast(count));
      await queryClient.invalidateQueries({ queryKey: ['media'] });
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    onError: (error) => {
      setUploadProgress(0);
      toast(roleActionErrorToast(error, 'No se pudo subir el archivo'));
      if (fileInputRef.current) fileInputRef.current.value = '';
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/media/files/${id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      setDeleteTarget(null);
      toast(mediaDeleteSuccessToast());
      await queryClient.invalidateQueries({ queryKey: ['media'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar el archivo'));
    },
  });

  function handleUpload(event: ChangeEvent<HTMLInputElement>) {
    const selected = event.target.files;
    if (!selected?.length || uploadMutation.isPending) return;
    setUploadProgress(0);
    uploadMutation.mutate(Array.from(selected));
  }

  const files = data?.data ?? [];
  const total = data?.meta?.total ?? files.length;
  const perPage = data?.meta?.perPage ?? 30;
  const totalPages = data?.meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);
  const hasQuery = Boolean(appliedSearch || activeType);

  return (
    <div className="lms-media-library lms-site-admin-hub-page font-sans">
      <header className="lms-media-library__header">
        <div className="flex items-start gap-4">
          <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Biblioteca Multimedia</h1>
            <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
              Gestor central de archivos — todos los módulos consumen desde aquí
            </p>
          </div>
        </div>
        {canUpload && (
        <label className="lms-media-library__upload">
          <span className="material-symbols-outlined" aria-hidden="true">
            upload
          </span>
          {uploadMutation.isPending ? 'Subiendo…' : 'Subir archivos'}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="sr-only"
            accept="*/*"
            disabled={uploadMutation.isPending}
            onChange={handleUpload}
          />
        </label>
        )}
      </header>

      {uploadMutation.isPending && (
        <div className="lms-media-library__upload-progress">
          <div className="lms-media-library__upload-bar">
            <div
              className="lms-media-library__upload-bar-fill"
              style={{ width: `${uploadProgress || 10}%` }}
            />
          </div>
          <p>
            Subiendo… <span>{uploadProgress}</span>%
          </p>
        </div>
      )}

      <section className="lms-media-library__filters">
        <form method="get" action="/media" className="lms-media-library__filters-row" onSubmit={(event) => event.preventDefault()}>
          <SearchInput
            name="search"
            value={search}
            onChange={setSearch}
            placeholder="Buscar por nombre o tipo..."
            aria-label="Buscar archivos"
            className="lms-media-library__search"
          />
        </form>

        <div className="lms-media-library__chips">
          {TYPE_CHIPS.map((chip) => (
            <button
              key={chip.value}
              type="button"
              className={`lms-media-library__chip ${activeType === chip.value ? 'lms-media-library__chip--active' : ''}`}
              onClick={() => setActiveType(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </section>

      <section className="lms-media-library__workspace">
        <aside className="lms-media-library__sidebar">
          <h3 className="lms-media-library__sidebar-title">Directorios</h3>
          <nav className="lms-media-library__folders">
            <span className="lms-media-library__folder lms-media-library__folder--active">
              <span className="material-symbols-outlined" aria-hidden="true">
                folder
              </span>
              <span>Raíz</span>
            </span>
          </nav>
        </aside>

        <div className="lms-media-library__main">
          {error && (
            <p className="mb-4 text-sm text-red-600" role="alert">
              {(error as Error).message}
            </p>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : files.length === 0 ? (
            hasQuery ? (
              <SearchEmptyState
                term={appliedSearch}
                onClear={() => {
                  setSearch('');
                  setActiveType('');
                }}
              />
            ) : (
              <div className="lms-media-library__empty">
                <EmptyState title="Sin archivos" description="Arrastra archivos aquí o usa el botón Subir." />
              </div>
            )
          ) : (
            <>
            <div className="lms-media-library__grid">
              {files.map((file) => {
                const name = displayName(file);
                const ext = name.split('.').pop()?.toUpperCase() ?? file.type?.toUpperCase() ?? 'FILE';
                const icon = TYPE_ICONS[file.type ?? ''] ?? 'draft';

                return (
                  <article key={file.id} className="lms-media-library__card group">
                    {(canEdit || canDelete) && (
                      <div className="lms-media-library__card-menu">
                        {canEdit && (
                          <Link
                            to={`/media/${file.id}`}
                            className="lms-media-library__more"
                            aria-label={`Editar ${name}`}
                            title="Editar"
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">
                              edit
                            </span>
                          </Link>
                        )}
                        {canDelete && (
                          <button
                            type="button"
                            className="lms-media-library__more lms-media-library__more--danger"
                            aria-label={`Eliminar ${name}`}
                            title="Eliminar"
                            onClick={() => setDeleteTarget(file)}
                          >
                            <span className="material-symbols-outlined" aria-hidden="true">
                              delete
                            </span>
                          </button>
                        )}
                      </div>
                    )}
                    <Link to={`/media/${file.id}`} className="lms-media-library__card-body">
                      <div className="lms-media-library__card-icon">
                        {file.thumbnailUrl ? (
                          <img src={file.thumbnailUrl} alt="" className="lms-media-library__card-thumb" />
                        ) : (
                          <span className="material-symbols-outlined" aria-hidden="true">
                            {icon}
                          </span>
                        )}
                      </div>
                      <div className="lms-media-library__card-meta">
                        <h4 title={name}>{name}</h4>
                        <div className="lms-media-library__card-info">
                          <span className="lms-media-library__ext">{ext}</span>
                          <span>{formatSize(file.size, file.humanSize)}</span>
                          <span className="lms-media-library__dot" aria-hidden="true" />
                          <span>v{file.versionCount ?? 1}</span>
                        </div>
                      </div>
                    </Link>
                  </article>
                );
              })}
            </div>
            <ListFooter
              page={page}
              totalPages={totalPages}
              total={total}
              start={rangeStart}
              end={rangeEnd}
              onPageChange={setPage}
            />
            </>
          )}
        </div>
      </section>

      <ConfirmModal
        open={Boolean(deleteTarget)}
        title="Eliminar archivo"
        description={
          deleteTarget
            ? `¿Está seguro de eliminar el archivo «${displayName(deleteTarget)}»? Esta acción no se puede deshacer.`
            : undefined
        }
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onClose={() => {
          if (!deleteMutation.isPending) setDeleteTarget(null);
        }}
      />
    </div>
  );
}
